/**
 * How large the enlarged teeth chart is drawn, and whether turning it helps.
 * The request of 5 October 2026 (third), section 5.
 *
 * The chart is wider than it is tall, about 600 by 370, and a phone held
 * upright is the other way about, so a chart fitted to the width leaves most of
 * the screen empty. Turned a quarter, its long side runs down the phone. Both
 * answers come from the same two measurements: the room the stage has, and the
 * size the chart is laid out at before anything scales it.
 *
 * View geometry, not a rule of the lab, so it sits beside the viewer and not in
 * the domain. Pure, with its test beside it.
 */

export interface Size {
  width: number;
  height: number;
}

/** Nothing has been laid out yet, or there is no layout at all, as in a test. */
function unmeasured(stage: Size, chart: Size): boolean {
  return stage.width <= 0 || stage.height <= 0 || chart.width <= 0 || chart.height <= 0;
}

/**
 * The scale at which the whole chart just fits the stage, upright or turned a
 * quarter. Turned, the chart's width runs down the stage and its height across.
 * 1 while there is nothing to measure, which draws the chart as it is laid out.
 */
export function chartFitScale(stage: Size, chart: Size, turned: boolean): number {
  if (unmeasured(stage, chart)) return 1;
  const across = turned ? chart.height : chart.width;
  const down = turned ? chart.width : chart.height;
  return Math.min(stage.width / across, stage.height / down);
}

/**
 * Whether the chart is larger turned than upright, which is when a Rotate
 * button is worth showing: on a phone held upright, and not on a desk or on a
 * phone lying on its side. True while there is nothing to measure, so the
 * button is there until a measurement says it is no use.
 */
export function turnHelps(stage: Size, chart: Size): boolean {
  if (unmeasured(stage, chart)) return true;
  return chartFitScale(stage, chart, true) > chartFitScale(stage, chart, false);
}
